import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  CpuChipIcon, 
  CalendarIcon, 
  ArrowPathIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { useTheme } from '../contexts/ThemeContext';
import { apiClient } from '../utils/api';
import { formatNumber, formatPercentage, getRiskLevelColor, getSignalColor, cn } from '../utils/helpers';
import { AIAnalysis as AIAnalysisType, TradingPosition } from '../types';
import io, { Socket } from 'socket.io-client';
import { getSocketUrl } from '../utils/api';

interface AIModelRecommendation {
  model: string;
  provider: string;
  recommendation: 'BUY' | 'SELL' | 'HOLD';
  confidence: number;
  reasoning: string;
  targetPrice: number;
  riskLevel: 'LOW' | 'MEDIUM' | 'HIGH';
}

interface TechnicalIndicators {
  rsi: number;
  macd: {
    value: number;
    signal: 'BULLISH' | 'BEARISH' | 'NEUTRAL';
    histogram: number;
    trend: string;
  };
  bollinger: {
    upper: number;
    lower: number;
    middle: number;
    position: 'UPPER' | 'MIDDLE' | 'LOWER';
    squeeze: boolean;
  };
  volume: {
    current: number;
    average: number;
    trend: 'HIGH' | 'NORMAL' | 'LOW';
    ratio: number;
  };
  sma: {
    sma20: number;
    sma50: number;
    sma200: number;
    trend: 'UPTREND' | 'DOWNTREND' | 'SIDEWAYS';
  };
  ema: {
    ema12: number;
    ema26: number;
    crossover: boolean;
  };
  stochastic: {
    k: number;
    d: number;
    signal: 'OVERSOLD' | 'OVERBOUGHT' | 'NEUTRAL';
  };
}

interface MarketSentiment {
  newsScore: number;
  socialScore: number;
  whaleActivity: 'HIGH' | 'MEDIUM' | 'LOW';
  fearGreedIndex: number;
  institutionalFlow: 'INFLOW' | 'OUTFLOW' | 'NEUTRAL';
  onChainMetrics: {
    networkValue: number;
    activeAddresses: number;
    exchangeFlow: number;
    hodlerRatio: number;
  };
  marketStructure: {
    funding: number;
    openInterest: number;
    derivatives: number;
  };
}

interface EnhancedAIAnalysis {
  signal: 'BUY' | 'SELL' | 'HOLD';
  confidence: number;
  reasoning: string;
  riskLevel: 'LOW' | 'MEDIUM' | 'HIGH';
  indicators: {
    rsi?: number;
    macd?: {
      macd: number;
      signal: number;
      histogram: number;
    };
    bollingerBands?: {
      upper: number;
      middle: number;
      lower: number;
    };
  };
  models: AIModelRecommendation[];
  consensus: {
    signal: 'BUY' | 'SELL' | 'HOLD';
    confidence: number;
    agreement: number;
  };
  technicalIndicators: TechnicalIndicators;
  marketSentiment: MarketSentiment;
  priceTargets: {
    support: number[];
    resistance: number[];
    pivotPoint: number;
  };
  riskMetrics: {
    stopLoss: number;
    riskReward: number;
    volatilityScore: number;
    maxDrawdown: number;
  };
  timestamp: string;
  timeframe: string;
  nextUpdateIn: number;
}

interface TabConfig {
  id: string;
  name: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  description: string;
}

const TAB_CONFIG: TabConfig[] = [
  { id: 'consensus', name: 'AI Consensus', icon: CpuChipIcon, color: 'text-blue-500', description: '다중 AI 모델 종합 분석' },
  { id: 'technical', name: 'Technical', icon: CalendarIcon, color: 'text-purple-500', description: '기술적 지표 분석' },
  { id: 'sentiment', name: 'Sentiment', icon: CpuChipIcon, color: 'text-green-500', description: '시장 심리 및 온체인 데이터' },
  { id: 'risk', name: 'Risk Analysis', icon: ExclamationTriangleIcon, color: 'text-red-500', description: '리스크 관리 및 손절매 설정' },
];

interface AIAnalysisProps {
  onNavigate?: (viewId: string) => void;
  onNavigateWithHighlight?: (viewId: string, highlight?: string) => void;
}

const AIAnalysis: React.FC<AIAnalysisProps> = ({ onNavigate, onNavigateWithHighlight }) => {
  const { theme } = useTheme();
  const [analysis, setAnalysis] = useState<EnhancedAIAnalysis | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('consensus');
  const [currentPosition, setCurrentPosition] = useState<TradingPosition | null>(null);
  const [socket, setSocket] = useState<Socket | null>(null);
  const [nextUpdateTime, setNextUpdateTime] = useState(300);
  const [showDetails, setShowDetails] = useState<Record<string, boolean>>({});

  // Fetch AI analysis
  const fetchAnalysis = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await apiClient.get<EnhancedAIAnalysis>('/api/analysis/enhanced');
      
      if (response.success && response.data) {
        setAnalysis(response.data);
      } else {
        throw new Error(response.error || 'Failed to fetch analysis');
      }
    } catch (err) {
      console.error('Error fetching AI analysis:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch analysis');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Setup WebSocket connection
  useEffect(() => {
    const socketInstance = io(getSocketUrl(), {
      path: '/socket.io/',
      transports: ['websocket', 'polling'],
    });

    socketInstance.on('connect', () => {
      console.log('AI Analysis connected to WebSocket');
    });

    socketInstance.on('aiAnalysis', (data: EnhancedAIAnalysis) => {
      setAnalysis(data);
      setNextUpdateTime(300);
    });

    socketInstance.on('position', (position: TradingPosition | null) => {
      setCurrentPosition(position);
    });

    setSocket(socketInstance);

    return () => {
      socketInstance.disconnect();
    };
  }, []);

  // Initial fetch and timer
  useEffect(() => {
    fetchAnalysis();
    
    const timer = setInterval(() => {
      setNextUpdateTime(prev => Math.max(0, prev - 1));
    }, 1000);

    const fetchTimer = setInterval(fetchAnalysis, 300000); // 5 minutes

    return () => {
      clearInterval(timer);
      clearInterval(fetchTimer);
    };
  }, [fetchAnalysis]);

  // Format time remaining
  const formatTimeRemaining = useMemo(() => {
    const minutes = Math.floor(nextUpdateTime / 60);
    const seconds = nextUpdateTime % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }, [nextUpdateTime]);

  // Toggle detail view
  const toggleDetails = useCallback((modelId: string) => {
    setShowDetails(prev => ({
      ...prev,
      [modelId]: !prev[modelId]
    }));
  }, []);

  // Render consensus tab
  const renderConsensusTab = useCallback(() => {
    if (!analysis) return null;

    return (
      <div className="space-y-6">
        {/* Consensus Summary */}
        <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-lg p-6 border border-blue-500/20">
          <h3 className="text-xl font-bold mb-4 flex items-center">
            <CpuChipIcon className="w-6 h-6 mr-2 text-blue-500" />
            AI 종합 분석 결과
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="text-center">
              <p className="text-sm text-gray-500 mb-1">신호</p>
              <p className={cn('text-2xl font-bold', getSignalColor(analysis.consensus.signal))}>
                {analysis.consensus.signal}
              </p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-500 mb-1">신뢰도</p>
              <p className="text-2xl font-bold">{formatPercentage(analysis.consensus.confidence, false)}</p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-500 mb-1">일치율</p>
              <p className="text-2xl font-bold">{formatPercentage(analysis.consensus.agreement, false)}</p>
            </div>
          </div>

          {/* Price Targets */}
          <div className="grid grid-cols-2 gap-4 p-4 bg-black/20 rounded-lg">
            <div>
              <p className="text-sm text-gray-500 mb-1">목표가</p>
              <p className="text-lg font-semibold text-green-500">
                ${formatNumber(analysis.priceTargets.resistance[0], 2)}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">손절가</p>
              <p className="text-lg font-semibold text-red-500">
                ${formatNumber(analysis.riskMetrics.stopLoss, 2)}
              </p>
            </div>
          </div>
        </div>

        {/* Individual Model Analysis */}
        <div className="space-y-4">
          <h4 className="text-lg font-semibold">개별 AI 모델 분석</h4>
          {analysis.models.map((model, index) => (
            <div
              key={`${model.provider}-${model.model}-${index}`}
              className={cn(
                'border rounded-lg p-4 transition-all',
                theme === 'dark' ? 'border-gray-700 hover:border-gray-600' : 'border-gray-200 hover:border-gray-300'
              )}
            >
              <div
                className="flex items-center justify-between cursor-pointer"
                onClick={() => toggleDetails(`${model.provider}-${index}`)}
              >
                <div className="flex items-center space-x-3">
                  <div className={cn('w-3 h-3 rounded-full', 
                    model.recommendation === 'BUY' ? 'bg-green-500' :
                    model.recommendation === 'SELL' ? 'bg-red-500' : 'bg-yellow-500'
                  )} />
                  <div>
                    <p className="font-semibold">{model.provider}</p>
                    <p className="text-sm text-gray-500">{model.model}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <span className={cn('font-bold', getSignalColor(model.recommendation))}>
                    {model.recommendation}
                  </span>
                  <span className="text-sm">
                    {formatPercentage(model.confidence, false)}
                  </span>
                  <ArrowPathIcon className={cn('w-4 h-4 transition-transform',
                    showDetails[`${model.provider}-${index}`] ? 'rotate-180' : ''
                  )} />
                </div>
              </div>
              
              {showDetails[`${model.provider}-${index}`] && (
                <div className="mt-4 pt-4 border-t border-gray-700">
                  <p className="text-sm mb-2">{model.reasoning}</p>
                  <div className="flex items-center justify-between text-sm">
                    <span>목표가: ${formatNumber(model.targetPrice, 2)}</span>
                    <span className={cn('font-semibold', getRiskLevelColor(model.riskLevel))}>
                      리스크: {model.riskLevel}
                    </span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }, [analysis, theme, showDetails, toggleDetails]);

  // Render technical tab
  const renderTechnicalTab = useCallback(() => {
    if (!analysis) return null;

    const { technicalIndicators: tech } = analysis;

    return (
      <div className="space-y-6">
        {/* Main Indicators */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* RSI */}
          <div className="border border-gray-700 rounded-lg p-4">
            <h4 className="font-semibold mb-3 flex items-center">
              <CalendarIcon className="w-5 h-5 mr-2 text-purple-500" />
              RSI (14)
            </h4>
            <div className="flex items-center justify-between">
              <span className={cn('text-2xl font-bold',
                tech.rsi > 70 ? 'text-red-500' :
                tech.rsi < 30 ? 'text-green-500' : 'text-gray-400'
              )}>
                {formatNumber(tech.rsi, 1)}
              </span>
              <span className="text-sm text-gray-500">
                {tech.rsi > 70 ? '과매수' : tech.rsi < 30 ? '과매도' : '중립'}
              </span>
            </div>
          </div>

          {/* MACD */}
          <div className="border border-gray-700 rounded-lg p-4">
            <h4 className="font-semibold mb-3">MACD</h4>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>MACD</span>
                <span className={tech.macd.value > 0 ? 'text-green-500' : 'text-red-500'}>
                  {formatNumber(tech.macd.value, 2)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Signal</span>
                <span className={cn(
                  tech.macd.signal === 'BULLISH' ? 'text-green-500' :
                  tech.macd.signal === 'BEARISH' ? 'text-red-500' : 'text-gray-400'
                )}>
                  {tech.macd.signal}
                </span>
              </div>
            </div>
          </div>

          {/* Bollinger Bands */}
          <div className="border border-gray-700 rounded-lg p-4">
            <h4 className="font-semibold mb-3">Bollinger Bands</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Upper</span>
                <span>${formatNumber(tech.bollinger.upper, 2)}</span>
              </div>
              <div className="flex justify-between">
                <span>Middle</span>
                <span>${formatNumber(tech.bollinger.middle, 2)}</span>
              </div>
              <div className="flex justify-between">
                <span>Lower</span>
                <span>${formatNumber(tech.bollinger.lower, 2)}</span>
              </div>
              {tech.bollinger.squeeze && (
                <p className="text-yellow-500 text-xs mt-2">⚠️ Squeeze 상태</p>
              )}
            </div>
          </div>

          {/* Stochastic */}
          <div className="border border-gray-700 rounded-lg p-4">
            <h4 className="font-semibold mb-3">Stochastic</h4>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>%K</span>
                <span>{formatNumber(tech.stochastic.k, 1)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>%D</span>
                <span>{formatNumber(tech.stochastic.d, 1)}</span>
              </div>
              <p className={cn('text-sm mt-2',
                tech.stochastic.signal === 'OVERBOUGHT' ? 'text-red-500' :
                tech.stochastic.signal === 'OVERSOLD' ? 'text-green-500' : 'text-gray-400'
              )}>
                {tech.stochastic.signal}
              </p>
            </div>
          </div>
        </div>

        {/* Moving Averages */}
        <div className="border border-gray-700 rounded-lg p-4">
          <h4 className="font-semibold mb-3">이동평균선</h4>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-gray-500 mb-1">SMA 20</p>
              <p className="font-semibold">${formatNumber(tech.sma.sma20, 2)}</p>
            </div>
            <div>
              <p className="text-gray-500 mb-1">SMA 50</p>
              <p className="font-semibold">${formatNumber(tech.sma.sma50, 2)}</p>
            </div>
            <div>
              <p className="text-gray-500 mb-1">SMA 200</p>
              <p className="font-semibold">${formatNumber(tech.sma.sma200, 2)}</p>
            </div>
          </div>
          <p className={cn('text-sm mt-3',
            tech.sma.trend === 'UPTREND' ? 'text-green-500' :
            tech.sma.trend === 'DOWNTREND' ? 'text-red-500' : 'text-gray-400'
          )}>
            추세: {tech.sma.trend}
          </p>
        </div>
      </div>
    );
  }, [analysis]);

  // Render sentiment tab
  const renderSentimentTab = useCallback(() => {
    if (!analysis) return null;

    const { marketSentiment: sentiment } = analysis;

    return (
      <div className="space-y-6">
        {/* Market Sentiment Scores */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="border border-gray-700 rounded-lg p-4">
            <h4 className="font-semibold mb-3">시장 심리 점수</h4>
            <div className="space-y-3">
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm">뉴스 감성</span>
                  <span className="text-sm">{formatNumber(sentiment.newsScore, 1)}/100</span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-2">
                  <div
                    className="bg-gradient-to-r from-red-500 to-green-500 h-2 rounded-full"
                    style={{ width: `${sentiment.newsScore}%` }}
                  />
                </div>
              </div>
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm">소셜 미디어</span>
                  <span className="text-sm">{formatNumber(sentiment.socialScore, 1)}/100</span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-2">
                  <div
                    className="bg-gradient-to-r from-red-500 to-green-500 h-2 rounded-full"
                    style={{ width: `${sentiment.socialScore}%` }}
                  />
                </div>
              </div>
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm">공포&탐욕 지수</span>
                  <span className="text-sm">{formatNumber(sentiment.fearGreedIndex, 1)}/100</span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-2">
                  <div
                    className={cn('h-2 rounded-full',
                      sentiment.fearGreedIndex < 25 ? 'bg-red-500' :
                      sentiment.fearGreedIndex < 50 ? 'bg-orange-500' :
                      sentiment.fearGreedIndex < 75 ? 'bg-yellow-500' : 'bg-green-500'
                    )}
                    style={{ width: `${sentiment.fearGreedIndex}%` }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="border border-gray-700 rounded-lg p-4">
            <h4 className="font-semibold mb-3">시장 활동</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>고래 활동</span>
                <span className={cn('font-semibold',
                  sentiment.whaleActivity === 'HIGH' ? 'text-red-500' :
                  sentiment.whaleActivity === 'MEDIUM' ? 'text-yellow-500' : 'text-green-500'
                )}>
                  {sentiment.whaleActivity}
                </span>
              </div>
              <div className="flex justify-between">
                <span>기관 자금 흐름</span>
                <span className={cn('font-semibold',
                  sentiment.institutionalFlow === 'INFLOW' ? 'text-green-500' :
                  sentiment.institutionalFlow === 'OUTFLOW' ? 'text-red-500' : 'text-gray-400'
                )}>
                  {sentiment.institutionalFlow}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* On-chain Metrics */}
        <div className="border border-gray-700 rounded-lg p-4">
          <h4 className="font-semibold mb-3">온체인 지표</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-gray-500 mb-1">네트워크 가치</p>
              <p className="font-semibold">${formatNumber(sentiment.onChainMetrics.networkValue, 0)}</p>
            </div>
            <div>
              <p className="text-gray-500 mb-1">활성 주소</p>
              <p className="font-semibold">{formatNumber(sentiment.onChainMetrics.activeAddresses, 0)}</p>
            </div>
            <div>
              <p className="text-gray-500 mb-1">거래소 유입</p>
              <p className="font-semibold">${formatNumber(sentiment.onChainMetrics.exchangeFlow, 0)}</p>
            </div>
            <div>
              <p className="text-gray-500 mb-1">홀더 비율</p>
              <p className="font-semibold">{formatPercentage(sentiment.onChainMetrics.hodlerRatio, false)}</p>
            </div>
          </div>
        </div>

        {/* Market Structure */}
        <div className="border border-gray-700 rounded-lg p-4">
          <h4 className="font-semibold mb-3">시장 구조</h4>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-gray-500 mb-1">펀딩률</p>
              <p className={cn('font-semibold',
                sentiment.marketStructure.funding > 0 ? 'text-green-500' : 'text-red-500'
              )}>
                {formatPercentage(sentiment.marketStructure.funding)}
              </p>
            </div>
            <div>
              <p className="text-gray-500 mb-1">미결제약정</p>
              <p className="font-semibold">${formatNumber(sentiment.marketStructure.openInterest, 0)}</p>
            </div>
            <div>
              <p className="text-gray-500 mb-1">파생상품 거래량</p>
              <p className="font-semibold">${formatNumber(sentiment.marketStructure.derivatives, 0)}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }, [analysis]);

  // Render risk tab
  const renderRiskTab = useCallback(() => {
    if (!analysis) return null;

    const { riskMetrics } = analysis;

    return (
      <div className="space-y-6">
        {/* Risk Overview */}
        <div className="bg-gradient-to-r from-red-500/10 to-orange-500/10 rounded-lg p-6 border border-red-500/20">
          <h3 className="text-xl font-bold mb-4 flex items-center">
            <ExclamationTriangleIcon className="w-6 h-6 mr-2 text-red-500" />
            리스크 분석
          </h3>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-gray-500 mb-1">손절가</p>
              <p className="text-lg font-bold text-red-500">
                ${formatNumber(riskMetrics.stopLoss, 2)}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">위험/보상 비율</p>
              <p className="text-lg font-bold">
                1:{formatNumber(riskMetrics.riskReward, 1)}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">변동성 점수</p>
              <p className={cn('text-lg font-bold',
                riskMetrics.volatilityScore > 70 ? 'text-red-500' :
                riskMetrics.volatilityScore > 40 ? 'text-yellow-500' : 'text-green-500'
              )}>
                {formatNumber(riskMetrics.volatilityScore, 0)}/100
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">최대 손실</p>
              <p className="text-lg font-bold text-red-500">
                {formatPercentage(riskMetrics.maxDrawdown)}
              </p>
            </div>
          </div>
        </div>

        {/* Position Sizing */}
        {currentPosition && (
          <div className="border border-gray-700 rounded-lg p-4">
            <h4 className="font-semibold mb-3">현재 포지션</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-500 mb-1">진입가</p>
                <p className="font-semibold">${formatNumber(currentPosition.entryPrice, 2)}</p>
              </div>
              <div>
                <p className="text-gray-500 mb-1">현재가</p>
                <p className="font-semibold">${formatNumber(currentPosition.markPrice, 2)}</p>
              </div>
              <div>
                <p className="text-gray-500 mb-1">미실현 손익</p>
                <p className={cn('font-semibold',
                  currentPosition.unrealizedProfit > 0 ? 'text-green-500' : 'text-red-500'
                )}>
                  ${formatNumber(currentPosition.unrealizedProfit, 2)}
                  ({formatPercentage(currentPosition.percentage)})
                </p>
              </div>
              <div>
                <p className="text-gray-500 mb-1">추천 액션</p>
                <p className={cn('font-semibold',
                  analysis.consensus.signal === 'BUY' ? 'text-green-500' :
                  analysis.consensus.signal === 'SELL' ? 'text-red-500' : 'text-yellow-500'
                )}>
                  {currentPosition.side === analysis.consensus.signal ? 'HOLD' : analysis.consensus.signal}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Risk Management Tips */}
        <div className="border border-gray-700 rounded-lg p-4">
          <h4 className="font-semibold mb-3">리스크 관리 가이드</h4>
          <ul className="space-y-2 text-sm">
            <li className="flex items-start">
              <span className="text-yellow-500 mr-2">•</span>
              <span>손절가를 ${formatNumber(riskMetrics.stopLoss, 2)}에 설정하여 최대 손실을 제한하세요.</span>
            </li>
            <li className="flex items-start">
              <span className="text-yellow-500 mr-2">•</span>
              <span>현재 변동성이 {riskMetrics.volatilityScore > 70 ? '높으므로' : riskMetrics.volatilityScore > 40 ? '보통이므로' : '낮으므로'} 포지션 크기를 {riskMetrics.volatilityScore > 70 ? '줄이는 것을' : '적절히 조절하는 것을'} 권장합니다.</span>
            </li>
            <li className="flex items-start">
              <span className="text-yellow-500 mr-2">•</span>
              <span>위험/보상 비율이 1:{formatNumber(riskMetrics.riskReward, 1)}이므로 {riskMetrics.riskReward >= 2 ? '좋은 거래 기회입니다.' : '신중한 접근이 필요합니다.'}</span>
            </li>
            <li className="flex items-start">
              <span className="text-yellow-500 mr-2">•</span>
              <span>전체 자본의 2% 이상을 한 거래에 투자하지 마세요.</span>
            </li>
          </ul>
        </div>
      </div>
    );
  }, [analysis, currentPosition]);

  // Tab content renderer
  const tabContent = useMemo(() => {
    switch (activeTab) {
      case 'consensus':
        return renderConsensusTab();
      case 'technical':
        return renderTechnicalTab();
      case 'sentiment':
        return renderSentimentTab();
      case 'risk':
        return renderRiskTab();
      default:
        return null;
    }
  }, [activeTab, renderConsensusTab, renderTechnicalTab, renderSentimentTab, renderRiskTab]);

  // Error state
  if (error) {
    return (
      <div className={cn(
        'rounded-lg p-6 text-center',
        theme === 'dark' ? 'bg-gray-800' : 'bg-white'
      )}>
        <ExclamationTriangleIcon className="w-12 h-12 mx-auto mb-4 text-red-500" />
        <p className="text-red-500 mb-4">분석 데이터를 불러올 수 없습니다</p>
        <p className="text-sm text-gray-500 mb-4">{error}</p>
        <button
          onClick={fetchAnalysis}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          다시 시도
        </button>
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className={cn(
        'rounded-lg p-6',
        theme === 'dark' ? 'bg-gray-800' : 'bg-white'
      )}>
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-700 rounded w-1/3" />
          <div className="space-y-2">
            <div className="h-4 bg-gray-700 rounded" />
            <div className="h-4 bg-gray-700 rounded w-5/6" />
            <div className="h-4 bg-gray-700 rounded w-4/6" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn(
      'rounded-lg',
      theme === 'dark' ? 'bg-gray-800' : 'bg-white'
    )}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-700">
        <h2 className="text-xl font-bold flex items-center">
          <CpuChipIcon className="w-6 h-6 mr-2 text-blue-500" />
          AI 종합 분석
        </h2>
        <div className="flex items-center space-x-4">
          <span className="text-sm text-gray-500">
            다음 업데이트: {formatTimeRemaining}
          </span>
          <button
            onClick={fetchAnalysis}
            className="p-2 rounded-lg hover:bg-gray-700 transition-colors"
            title="새로고침"
          >
            <ArrowPathIcon className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-700">
        <div className="flex space-x-1 p-2">
          {TAB_CONFIG.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                'flex items-center px-4 py-2 rounded-lg transition-all',
                activeTab === tab.id
                  ? theme === 'dark' 
                    ? 'bg-gray-700 text-white' 
                    : 'bg-gray-100 text-gray-900'
                  : theme === 'dark'
                    ? 'text-gray-400 hover:text-white hover:bg-gray-700/50'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              )}
            >
              <tab.icon className={cn('w-5 h-5 mr-2', activeTab === tab.id && tab.color)} />
              <span className="font-medium">{tab.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {tabContent}
      </div>
    </div>
  );
};

export default React.memo(AIAnalysis);